import type { TicketPurchaseWithPurchaser } from "../../requests/ticketPurchases"
import type { EventInformationPost } from "./EventInformation"

// Minimal, duck-typed subset of PayoutData (see PayoutDistributionPage/types.ts) -
// kept independent here so this shared CSV export doesn't depend on that page's types.
export interface PayoutCSVDistribution {
  band_name: string
  pending_band_email?: string | null
  recipient_email?: string | null
  role?: string | null
  amount: number
  status: string
}

export interface PayoutCSVInfo {
  status: string
  band_share: number
  bn_share: number
  waived_bn_fees: number
  distributed_amount: number
  remaining_band_share: number
  released_at?: string | null
  updated_at?: string | null
  payout_distributions?: PayoutCSVDistribution[]
}

interface CSVExportData {
  post: EventInformationPost
  tickets: TicketPurchaseWithPurchaser[]
  stats: {
    total: number
    redeemed: number
    unredeemed: number
  }
  payout?: PayoutCSVInfo | null
}

const escapeCell = (cell: string) =>
  /[",\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell

const formatDateTime = (dateString?: string | null): string => {
  if (!dateString) return ""
  return new Date(dateString).toLocaleString()
}

// Builds a "Payout" section reflecting whatever the payout's current state is -
// not yet released, released, partially distributed, or fully sent/completed.
const buildPayoutRows = (payout: PayoutCSVInfo): string[][] => {
  const rows: string[][] = [
    ["Payout"],
    ["Status", payout.status],
    ["Total Payout", `$${payout.band_share}`],
    ["bandNada Fee", `$${payout.bn_share}`],
  ]

  if (payout.waived_bn_fees > 0) {
    rows.push(["Waived bandNada Fees", `$${payout.waived_bn_fees}`])
  }

  if (payout.released_at) {
    rows.push(["Released", formatDateTime(payout.released_at)])
  }

  if (payout.distributed_amount > 0) {
    rows.push(["Distributed to Others", `$${payout.distributed_amount}`])
    if (payout.payout_distributions?.length) {
      rows.push(["Recipient", "Email", "Role", "Amount", "Status"])
      payout.payout_distributions.forEach((dist) => {
        rows.push([
          dist.band_name || "",
          dist.recipient_email || dist.pending_band_email || "",
          dist.role || "",
          `$${dist.amount}`,
          dist.status,
        ])
      })
    }
    rows.push(["Remaining/Your Share", `$${payout.remaining_band_share}`])
  }

  if (payout.status === "completed") {
    rows.push(["Sent", formatDateTime(payout.updated_at)])
  }

  rows.push([], [])
  return rows
}

export const generateTicketsCSV = ({ post, tickets, stats, payout }: CSVExportData) => {
  // Get event info values (prefer event_ticket_summary when available)
  const summary = post.event_ticket_summary
  const ticketPrice = summary?.ticket_price ?? post.ticket_price
  const maxTickets = summary?.max_tickets ?? post.max_tickets
  const ticketsSold = summary?.tickets_sold ?? post.tickets_sold
  const ticketsRemaining = summary?.tickets_remaining ?? post.tickets_remaining
  const currentEstimatedPayout =
    summary?.current_estimated_payout ?? post.current_estimated_payout
  const locationDisplay =
    typeof post.location === "string"
      ? post.location
      : post.location?.displayName || ""

  // Cash ticket values
  const cashTicketsPurchased = post.content_hash?.["cash_tickets_purchased"]
  const cashTicketsTotal = post.content_hash?.["cash_tickets_total"]

  // Build info rows
  const infoRows: string[][] = [
    ["Event Information"],
    ["Event Title", post.title || ""],
    ["Start Time", formatDateTime(post.start_dt)],
    ["End Time", formatDateTime(post.end_dt)],
    ["Location", locationDisplay],
    ["Ticket Price", ticketPrice != null ? `$${ticketPrice}` : ""],
    ["Max Tickets", maxTickets != null ? maxTickets.toString() : ""],
    ["Tickets Sold", ticketsSold != null ? ticketsSold.toString() : ""],
    [
      "Tickets Remaining",
      ticketsRemaining != null ? ticketsRemaining.toString() : "",
    ],
    [
      "Current Est. Payout",
      currentEstimatedPayout != null ? `$${currentEstimatedPayout}` : "",
    ],
    [],
    ["Stats"],
    ["Total Tickets", stats.total.toString()],
    ["Redeemed", stats.redeemed.toString()],
    ["Unredeemed", stats.unredeemed.toString()],
    [],
    ["Cash Tickets"],
    [
      "Cash Tickets Qty",
      cashTicketsPurchased != null
        ? Number(cashTicketsPurchased).toString()
        : "0",
    ],
    [
      "Cash Tickets Total",
      cashTicketsTotal != null ? `$${Number(cashTicketsTotal)}` : "$0",
    ],
    [],
    [], // Extra blank line before ticket table
  ]

  const csvHeaders = [
    "Ticket ID",
    "First Name",
    "Last Name",
    "Email",
    "Purchase Date",
    "Amount",
    "Redeemed",
  ]

  const csvRows = tickets.map((ticket) => [
    ticket.ticket_id || "",
    ticket.purchaser?.first_name || "",
    ticket.purchaser?.last_name || "",
    ticket.purchaser?.email || "",
    ticket.created_at ? new Date(ticket.created_at).toLocaleDateString() : "",
    ticket.amount != null ? `$${ticket.amount.toString()}` : "",
    ticket.redeemed ? "Yes" : "No",
  ])

  const payoutRows = payout ? buildPayoutRows(payout) : []

  return [
    ...infoRows.map((row) => row.map(escapeCell).join(",")),
    ...payoutRows.map((row) => row.map(escapeCell).join(",")),
    csvHeaders.join(","),
    ...csvRows.map((row) => row.map(escapeCell).join(",")),
  ].join("\n")
}

export const downloadCSV = (csvContent: string, filename: string) => {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
  const link = document.createElement("a")
  const url = URL.createObjectURL(blob)
  link.setAttribute("href", url)
  link.setAttribute("download", filename)
  link.style.visibility = "hidden"
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
